import React, { useEffect, useState } from 'react'
import '../../App.css'
import { Dropdown, Modal, Button } from 'react-bootstrap'
import Axios from 'axios'
import ReactTooltip from 'react-tooltip'
import { BsClockFill, BsFillCircleFill } from 'react-icons/bs'
import {
  BiCheckCircle,
  BiCircle,
  BiErrorAlt,
  BiHide,
  BiHourglass,
  BiInfoCircle,
  BiRefresh,
  BiShow,
} from 'react-icons/bi'
import { IoSync } from 'react-icons/io5'
import { formatZARCurrency } from '../../MoneyFormat'
import { API_URL } from '../../config'

/* ------------------------------------------------------------------ */
/* Payout rules. Change here only.                                     */
/* IMPORTANT: the backend (REQUESTPAYOUT) must recompute the fee with  */
/* the same rules and ignore the fee sent from the browser.            */
/* ------------------------------------------------------------------ */
const MIN_PAYOUT = 100
const FEE_TIER_THRESHOLD = 200 // amounts below this pay FEE_LOW, amounts at/above pay FEE_HIGH
const FEE_LOW = 15
const FEE_HIGH = 20
const ADVANCE_PERCENT = 30 // % released before the event ends
const VERIFY_HOURS = 48 // verification window after the event ends

const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100
const getPayoutFee = (amount) => (Number(amount) >= FEE_TIER_THRESHOLD ? FEE_HIGH : FEE_LOW)
const zar = (n) => formatZARCurrency(String(round2(n)))
const toTitleCase = (str = '') =>
  String(str)
    .toLowerCase()
    .replace(/(^|[\s\-–(/])([a-z])/g, (_, sep, ch) => sep + ch.toUpperCase())

/* ------------------------------------------------------------------ */
/* "How payouts work" modal                                            */
/* ------------------------------------------------------------------ */
const PayoutInfoModal = ({ isOpen, onClose }) => (
  <Modal show={isOpen} onHide={onClose} centered scrollable>
    <Modal.Header closeButton>
      <Modal.Title style={{ fontSize: 18 }}>How payouts work</Modal.Title>
    </Modal.Header>
    <Modal.Body style={{ fontSize: 14 }}>
      <ul className="pl-3 mb-0">
        <li className="mb-2">
          Karamale releases {ADVANCE_PERCENT}% of your net sales before the event to help cover
          costs. The remaining {100 - ADVANCE_PERCENT}% is held until the event ends.
        </li>
        <li className="mb-2">
          After the event ends, Karamale verifies that the event took place, which usually completes
          within {VERIFY_HOURS} hours. This protects ticket buyers and organisers. Once verified,
          your balance is released. In some cases part of the balance stays held until verification
          is complete.
        </li>
        <li className="mb-2">
          Minimum payout: <strong>{zar(MIN_PAYOUT)}</strong>.
        </li>
        <li className="mb-2">
          Payout fee: <strong>{zar(FEE_LOW)}</strong> for payouts under {zar(FEE_TIER_THRESHOLD)},{' '}
          <strong>{zar(FEE_HIGH)}</strong> for payouts of {zar(FEE_TIER_THRESHOLD)} or more. The fee
          is charged per payout request and deducted from the amount you request.
        </li>
        <li>
          The payout fee applies to{' '}
          <strong>all events, including events sponsored by Karamale</strong>. Partial payouts are
          charged the fee each time, so fewer, larger payouts cost less.
        </li>
      </ul>
    </Modal.Body>
    <Modal.Footer>
      <Button variant="info" onClick={onClose} style={{ fontWeight: 800 }}>
        Got it
      </Button>
    </Modal.Footer>
  </Modal>
)

/* ------------------------------------------------------------------ */
/* Payout modal (full + partial)                                       */
/* ------------------------------------------------------------------ */
const PayoutModal = ({
  isOpen,
  onClose,
  onConfirm,
  available,
  payOutType,
  sponsored,
  loading,
  releasePercent,
  held,
  released,
  heldLabel,
  releaseSummary,
  bank,
}) => {
  const isFull = payOutType === 'full'
  const [amount, setAmount] = useState('')
  const [bankConfirmed, setBankConfirmed] = useState(false)
  const [showBankError, setShowBankError] = useState(false)
  const [fullNumber, setFullNumber] = useState(null)
  const [revealLoading, setRevealLoading] = useState(false)
  const [revealError, setRevealError] = useState('')

  useEffect(() => {
    if (isOpen) {
      setAmount(isFull ? String(round2(available)) : '')
      setBankConfirmed(false)
      setShowBankError(false)
    }
    // always start masked, and drop the full number from memory when the modal closes
    setFullNumber(null)
    setRevealError('')
  }, [isOpen, isFull, available])

  const num = round2(amount)
  const fee = getPayoutFee(num)
  const receive = round2(num - fee)
  const remaining = round2(available - num)

  let error = ''
  if (!isFull && amount !== '') {
    if (num < MIN_PAYOUT) error = `Minimum payout is ${zar(MIN_PAYOUT)}.`
    else if (num > available) error = `You can withdraw at most ${zar(available)}.`
  }
  const valid = num >= MIN_PAYOUT && num <= available && !error
  const hasBank = !!(bank && bank.account_masked)

  const toggleReveal = () => {
    if (fullNumber) {
      setFullNumber(null)
      return
    }
    setRevealLoading(true)
    setRevealError('')
    Axios.get(`${API_URL}/apps/kscanner/webservices.php?query=REVEALBANKACCOUNT`, {
      withCredentials: true,
    })
      .then((res) => {
        if (res.data.status === 'success' && res.data.account_number) {
          setFullNumber(String(res.data.account_number))
        } else {
          setRevealError("Couldn't load your account number.")
        }
      })
      .catch(() => setRevealError("Couldn't load your account number."))
      .finally(() => setRevealLoading(false))
  }

  const handleConfirm = () => {
    if (!bankConfirmed) {
      setShowBankError(true)
      return
    }
    onConfirm(num)
  }

  const row = { display: 'flex', justifyContent: 'space-between', padding: '6px 0' }

  return (
    <>
      <style>{`
      .payout-modal.modal { overflow-y: auto !important; }
      .payout-modal .modal-dialog {
        margin: 0.5rem auto !important;
        max-height: calc(100vh - 1rem) !important;
        max-height: calc(100dvh - 1rem) !important;
        display: flex !important;
        align-items: center !important;
      }
      .payout-modal .modal-content {
        max-height: calc(100vh - 1rem) !important;
        max-height: calc(100dvh - 1rem) !important;
        display: flex !important;
        flex-direction: column !important;
        width: 100%;
      }
      .payout-modal .modal-header,
      .payout-modal .modal-footer { flex: 0 0 auto !important; }
      .payout-modal .modal-body {
        flex: 1 1 auto !important;
        min-height: 0 !important;
        max-height: none !important;
        overflow-y: auto !important;
        -webkit-overflow-scrolling: touch;
        overscroll-behavior: contain;
      }
    `}</style>
      <Modal show={isOpen} onHide={onClose} centered className="payout-modal">
        <Modal.Header closeButton>
          <Modal.Title style={{ fontSize: 18 }}>
            {isFull ? 'Full payout' : 'Partial payout'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div
            style={{
              border: '1px solid #e3e7ec',
              borderRadius: 8,
              padding: '10px 14px',
              marginBottom: 14,
              fontSize: 14,
            }}
          >
            <div style={row}>
              <span>Released to you ({releasePercent}%)</span>
              <span>{zar(released)}</span>
            </div>
            <div style={row}>
              <span>
                {heldLabel} ({100 - releasePercent}%)
              </span>
              <span>{zar(held)}</span>
            </div>
            <div style={{ ...row, borderTop: '1px solid #e3e7ec', fontWeight: 700 }}>
              <span>Available to withdraw now</span>
              <span style={{ color: '#1b8a5a' }}>{zar(available)}</span>
            </div>
            <small className="text-muted">{releaseSummary}</small>
          </div>
          {isFull ? (
            <div style={row}>
              <span>Amount to withdraw</span>
              <strong>{zar(available)}</strong>
            </div>
          ) : (
            <div className="mb-3">
              <label className="mb-1">
                <strong>Amount to withdraw (R)</strong>
              </label>
              <input
                type="number"
                inputMode="decimal"
                min={MIN_PAYOUT}
                max={available}
                step="0.01"
                className="form-control"
                placeholder={`${MIN_PAYOUT} – ${round2(available)}`}
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                autoFocus
              />
              <small className="text-muted">
                Available now: {zar(available)} · Minimum: {zar(MIN_PAYOUT)}
              </small>
              {error && <div className="text-danger mt-1">{error}</div>}
            </div>
          )}

          <div style={{ background: '#f6f8fa', borderRadius: 8, padding: '10px 14px' }}>
            <div style={row}>
              <span>Amount requested</span>
              <span>{num > 0 ? zar(num) : '—'}</span>
            </div>
            <div style={row}>
              <span>
                Payout fee{' '}
                <small className="text-muted">
                  (
                  {num >= FEE_TIER_THRESHOLD
                    ? `R${FEE_TIER_THRESHOLD}+`
                    : `under R${FEE_TIER_THRESHOLD}`}
                  )
                </small>
              </span>
              <span className="text-danger">{num > 0 ? `− ${zar(fee)}` : '—'}</span>
            </div>
            <div
              style={{
                ...row,
                borderTop: '1px solid #dde2e8',
                marginTop: 4,
                paddingTop: 10,
                fontSize: 17,
              }}
            >
              <strong>You receive in your bank</strong>
              <strong>{valid ? zar(receive) : '—'}</strong>
            </div>
            {!isFull && valid && (
              <div style={{ ...row, color: '#6c757d' }}>
                <span>Still available after this payout</span>
                <span>{zar(remaining)}</span>
              </div>
            )}
          </div>

          {hasBank ? (
            <div
              style={{
                border: '1px solid #e3e7ec',
                borderRadius: 8,
                padding: '10px 14px',
                marginTop: 14,
                fontSize: 14,
              }}
            >
              <div
                style={{
                  fontSize: 12,
                  color: '#6c757d',
                  textTransform: 'uppercase',
                  letterSpacing: 0.4,
                }}
              >
                Payout will be sent to
              </div>
              <div className="mt-1">
                <strong>{bank.bank_name}</strong>
                {bank.account_type ? ` · ${bank.account_type}` : ''}
              </div>
              {bank.account_holder && <div>{bank.account_holder}</div>}
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ letterSpacing: fullNumber ? 0.5 : 0 }}>
                  {fullNumber || bank.account_masked}
                </span>
                <button
                  type="button"
                  onClick={toggleReveal}
                  disabled={revealLoading}
                  title={fullNumber ? 'Hide account number' : 'Show account number'}
                  aria-label={fullNumber ? 'Hide account number' : 'Show account number'}
                  style={{
                    background: 'none',
                    border: 0,
                    padding: 0,
                    margin: 0,
                    lineHeight: 1,
                    color: '#6c757d',
                    cursor: revealLoading ? 'wait' : 'pointer',
                  }}
                >
                  {fullNumber ? <BiHide size={20} /> : <BiShow size={20} />}
                </button>
              </div>
              {revealError && <div className="text-danger text-12">{revealError}</div>}
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'flex-start',
                  gap: 10,
                  marginTop: 10,
                }}
              >
                <input
                  id="confirm-bank-details"
                  type="checkbox"
                  checked={bankConfirmed}
                  onChange={(e) => {
                    setBankConfirmed(e.target.checked)
                    if (e.target.checked) setShowBankError(false)
                  }}
                  style={{
                    appearance: 'auto',
                    WebkitAppearance: 'checkbox',
                    width: 18,
                    height: 18,
                    minWidth: 18,
                    flex: 'none',
                    margin: 0,
                    padding: 0,
                    cursor: 'pointer',
                    accentColor: '#17a2b8',
                  }}
                />
                <label
                  htmlFor="confirm-bank-details"
                  style={{
                    margin: 0,
                    width: 'auto',
                    fontWeight: 400,
                    cursor: 'pointer',
                  }}
                >
                  <span style={{ fontWeight: 400 }}>I confirm these bank details are correct</span>
                </label>
              </div>
              {showBankError && !bankConfirmed && (
                <div className="text-danger mt-1">
                  Please confirm your bank details are correct to continue.
                </div>
              )}
              <small className="text-muted d-block mt-1">
                Wrong details? <a href="./edit-b-details">Update your bank details</a>
              </small>
            </div>
          ) : (
            <div className="alert alert-warning mt-3 mb-0">
              <strong>No bank account added.</strong>
              <br />
              <a href="./edit-b-details">Add your bank details</a> to request a payout.
            </div>
          )}

          <small className="text-muted d-block mt-2">
            The payout fee is deducted from the amount you request.
            {sponsored && ' The payout fee also applies to events sponsored by Karamale.'}
          </small>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="light" onClick={onClose} disabled={loading}>
            Cancel
          </Button>
          <Button
            variant="info"
            disabled={!hasBank || !valid || loading}
            onClick={handleConfirm}
            style={{ fontWeight: 800 }}
          >
            {loading ? 'Requesting...' : `Request ${valid ? zar(num) : 'payout'}`}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  )
}

/* ------------------------------------------------------------------ */
/* Verification badge + banner. Deliberately neutral: no scores or     */
/* signals shown.                                                      */
/*                                                                     */
/* Backend contract: verification.state is one of                      */
/*   'hold'     advance on hold (can be sent BEFORE the event ends)    */
/*   'window'   verification in progress (after event ends)            */
/*   'review'   under review (after event ends)                        */
/*   'partial'  partially released (after event ends, uses release_pct)*/
/*   'verified' fully verified (after event ends)                      */
/* Anything else is treated as 'none'.                                 */
/*                                                                     */
/* verification.reason (optional): a short, organiser-friendly         */
/* sentence chosen on the backend. Never send internal codes, scores   */
/* or signal names. Shown in the hold, review and partial banners.     */
/* ------------------------------------------------------------------ */
const VERIFY_BADGE = {
  hold: { label: 'Advance on hold', bg: '#fff1e0', color: '#b25e00' },
  window: { label: 'Verification in progress', bg: '#e8f1fb', color: '#1f5fa8' },
  review: { label: 'Under review', bg: '#fff1e0', color: '#b25e00' },
  partial: { label: 'Partially released', bg: '#e8f1fb', color: '#1f5fa8' },
  verified: { label: 'Event verified', bg: '#e6f5ee', color: '#1b8a5a' },
}

const VerificationBadge = ({ state }) => {
  const b = VERIFY_BADGE[state]
  if (!b) return null
  return (
    <span
      style={{
        display: 'inline-block',
        background: b.bg,
        color: b.color,
        fontSize: 12,
        fontWeight: 700,
        padding: '4px 10px',
        borderRadius: 12,
        marginLeft: 10,
        verticalAlign: 'middle',
      }}
    >
      {b.label}
    </span>
  )
}

/* ------------------------------------------------------------------ */
/* Summary page                                                        */
/* ------------------------------------------------------------------ */
const Summary = (props) => {
  const [alertState, setAlertState] = useState('success-alert')
  const [resMessage, setResMessage] = useState('')
  const [errorDisplay, setErrorDisplay] = useState('d-none')
  const [payOutType, setPayOutType] = useState('')
  const [sales, setSales] = useState(0)
  const [deduction, setDeduction] = useState(0)
  const [eventEndDate, setEventEndDate] = useState('0000-00-00')
  const [payoutStatus, setPayoutStatus] = useState('No Activity')
  const [amountRefunded, setAmountRefunded] = useState(0)
  const [count, setCount] = useState(0)
  const [refreshing, setRefreshing] = useState(false)
  const [status, setStatus] = useState('')
  const [isSponsorship, setIsSponsorship] = useState(0)
  const [isEventEnded, setIsEventEnded] = useState('no')
  const [alreadyWithdrawn, setAlreadyWithdrawn] = useState(0)
  const [net, setNet] = useState(0)
  const [gross, setGross] = useState(0)
  const [message, setMessage] = useState('')
  const [clipLoading, setClipLoading] = useState(false)
  const [modalOpen, setModalOpen] = useState(false)
  const [infoOpen, setInfoOpen] = useState(false)
  const [eventName, setEventName] = useState('')
  const [serverAvailable, setServerAvailable] = useState(null)
  const [verification, setVerification] = useState(null)
  const [bank, setBank] = useState(null)

  const event_id = props.eventid

  useEffect(() => {
    const businessid = localStorage.getItem('businessid')
    Axios.get(
      `${API_URL}/apps/kscanner/webservices.php?query=PAYOUTSUMMARY&businessid=${businessid}&event_id=${event_id}`,
      { withCredentials: true },
    )
      .then((response) => {
        const res = response.data.results[0]
        const responseData = response.data
        setStatus(responseData.status)
        setSales(res.total_sales ?? 0.0)
        // total_net from the backend already has payouts requested subtracted, which breaks the 30% maths.
        // Use the full sales figure (net_payout) and subtract requested payouts after the release %.
        setNet(res.gross_sales ?? 0.0)
        setGross(res.gross_sales ?? 0.0)
        setDeduction(res.total_fees_deduction ?? 0.0)
        setEventEndDate(res.event_end_date ?? '0000-00-00')
        setPayoutStatus(res.payout_status ?? 'No Activity')
        setAmountRefunded(responseData.amount_refunded ?? 0.0)
        setIsSponsorship(responseData.is_sponsorship ?? 0)
        setMessage(res.message ?? '')
        setRefreshing(false)
        setIsEventEnded(responseData.isEventEnded)
        setAlreadyWithdrawn(responseData.amountRequest ?? 0)
        setServerAvailable(responseData.available_to_withdraw ?? null)
        // keep only the whitelisted fields, so nothing else the backend sends is held in state
        const v = responseData.verification
        setVerification(
          v
            ? {
                state: v.state,
                release_pct: v.release_pct,
                opens_at_label: v.opens_at_label,
                reason: typeof v.reason === 'string' ? v.reason : null,
              }
            : null,
        )
        // masked details only. The backend must never send the full account number.
        const b = responseData.bank_account
        setBank(
          b
            ? {
                id: b.id,
                bank_name: b.bank_name,
                account_holder: b.account_holder,
                account_type: b.account_type,
                account_masked: b.account_masked,
              }
            : null,
        )
        setEventName(
          props.eventName ?? props.eventname ?? res.event_name ?? responseData.event_name ?? '',
        )
      })
      .catch((error) => console.log(error))
  }, [count, props.count])

  /* ---------------- calculations ---------------- */
  const sponsored = Number(isSponsorship) === 1
  const eventEnded = isEventEnded === 'yes'
  const refunded = Number(amountRefunded) || 0
  const withdrawn = Number(alreadyWithdrawn) || 0

  // 'hold' applies at any time (including mid-sales). Every other state only counts after the event ends.
  const rawState = verification?.state ?? 'none'
  const verState = rawState === 'hold' ? 'hold' : eventEnded ? rawState : 'none'
  const onHold = verState === 'hold'
  const verifying = verState === 'window' || verState === 'review'
  const blocked = onHold || verifying // nothing can be withdrawn
  const partialVerified = verState === 'partial'
  const verPct = Number(verification?.release_pct) || 0
  const verReason = verification?.reason || ''

  const netAfterRefunds = Math.max(0, Number(net) - refunded)
  let releasePercent = eventEnded ? 100 : ADVANCE_PERCENT
  if (blocked) releasePercent = 0
  else if (partialVerified) releasePercent = verPct
  const released = netAfterRefunds * (releasePercent / 100)
  const held = Math.max(0, netAfterRefunds - released)
  const releasedRemaining = Math.max(0, released - withdrawn)

  const platformFee = Number(deduction) // 3.5% on online sales only; physical tickets are paid upfront
  const platformFeeApplied = sponsored ? 0 : platformFee

  const localAvailable = round2(Math.max(0, releasedRemaining - platformFeeApplied))
  // the server's figure is the one the payout request enforces, so show that when it is provided
  const netPayout = blocked
    ? 0
    : serverAvailable !== null
      ? round2(serverAvailable)
      : localAvailable // available to withdraw
  const canRequest = netPayout >= MIN_PAYOUT

  const fullFee = getPayoutFee(netPayout)
  const fullReceive = round2(netPayout - fullFee)

  /* ---------------- release wording ---------------- */
  const heldLabel = onHold
    ? 'Held while on hold'
    : eventEnded
      ? 'Held pending verification'
      : 'Held until event ends'

  let releaseSummary
  if (verState === 'hold') {
    releaseSummary = 'Your advance is on hold. Please contact support.'
  } else if (verState === 'window') {
    releaseSummary = 'Payouts open once verification of your event is complete.'
  } else if (verState === 'review') {
    releaseSummary = 'Your payout is under review.'
  } else if (partialVerified) {
    releaseSummary = `${verPct}% of your net sales is released now. The remaining ${
      100 - verPct
    }% is released once verification is complete.`
  } else if (eventEnded) {
    releaseSummary = 'The event has ended, so your full balance is released.'
  } else {
    releaseSummary = `Only ${ADVANCE_PERCENT}% is released until the event ends. The rest becomes available after the event ends and is verified.`
  }

  let releaseLine
  if (onHold) releaseLine = `${zar(held)} held, advance on hold`
  else if (verifying) releaseLine = `${zar(held)} held pending verification`
  else if (partialVerified)
    releaseLine = `${releasePercent}% released · ${zar(held)} held pending verification`
  else if (eventEnded) releaseLine = 'Event ended: 100% released'
  else releaseLine = `${releasePercent}% released · ${zar(held)} held until event ends`

  /* ---------------- request payout ---------------- */
  const flash = (type, text) => {
    setAlertState(type)
    setResMessage(text)
    setErrorDisplay('d-block')
    setTimeout(() => setErrorDisplay('d-none'), 5000)
  }

  const handlePayoutRequest = (amountRequested) => {
    const amount = round2(amountRequested)
    if (!amount || amount < MIN_PAYOUT) {
      setModalOpen(false)
      flash('error-alert', `Minimum payout is R${MIN_PAYOUT}`)
      return
    }
    if (amount > netPayout) {
      setModalOpen(false)
      flash('error-alert', 'Amount cannot exceed your available balance')
      return
    }

    const payoutFee = getPayoutFee(amount)
    const amountReceived = round2(amount - payoutFee)

    // NOTE: the backend must ignore payoutFee, amountReceived, net and gross from the browser
    // and recompute all of them (and the available balance) itself.
    const data = new FormData()
    data.append('amountRequested', amount)
    data.append('payoutFee', payoutFee)
    data.append('amountReceived', amountReceived)
    data.append('net', netPayout)
    data.append('gross', gross)
    data.append('event_id', event_id)
    data.append('payout_type', payOutType)
    // The backend must pay to the account it has on file and reject the request if bank_account_id
    // no longer matches (details changed after the page loaded). Never accept account numbers from the browser.
    data.append('bank_account_id', bank?.id ?? '')
    data.append('bank_confirmed', '1')
    data.append('source', 'ORGANISER_PAYOUT')
    data.append('query', 'REQUESTPAYOUT')
    setClipLoading(true)

    Axios({
      method: 'POST',
      url: `${API_URL}/apps/kscanner/webservices.php`,
      data,
      withCredentials: true,
    })
      .then((response) => {
        const responseData = response.data
        if (responseData.status === 'success') {
          setCount(count + 1)
          flash('success-alert', responseData.message)
        } else {
          flash('error-alert', responseData.message)
        }
        setModalOpen(false)
        setClipLoading(false)
      })
      .catch((error) => {
        setClipLoading(false)
        alert(error)
        console.log(error)
      })
  }

  const requestPayout = (type) => {
    setPayOutType(type)
    setCount(count + 1) // refetch so bank details and balance are current when the modal opens
    setModalOpen(true)
  }

  /* ---------------- status badges ---------------- */
  const today = new Date()
  const currentDate = today.toISOString().split('T')[0]
  const eventOver = currentDate > eventEndDate

  let isComplete
  if (eventOver) {
    isComplete =
      sales > 0 ? (
        <span>
          <BiCheckCircle size={20} color="green" />
          &nbsp;&nbsp;Completed
        </span>
      ) : (
        <span>
          <BiHourglass size={20} color="#5A7D9A" />
          &nbsp;&nbsp;Awaiting Sales
        </span>
      )
  } else {
    isComplete = (
      <span>
        <BsFillCircleFill size={20} color="gold" />
        &nbsp;&nbsp;In Progress
      </span>
    )
  }

  let payout_status
  if (verState === 'hold') {
    payout_status = (
      <span>
        <BiHourglass size={20} color="#b25e00" />
        &nbsp;&nbsp;Advance on hold
      </span>
    )
  } else if (
    verState === 'review' ||
    String(payoutStatus).toLowerCase() === 'payoutpendinginvestigation'
  ) {
    payout_status = (
      <span>
        <BiHourglass size={20} color="#b25e00" />
        &nbsp;&nbsp;Under review
      </span>
    )
  } else if (payoutStatus === 'Pending') {
    payout_status = (
      <span>
        <BsClockFill size={20} color="orange" />
        &nbsp;&nbsp;{payoutStatus}
      </span>
    )
  } else if (payoutStatus === 'Processing') {
    payout_status = (
      <span>
        <IoSync size={20} color="#2196f3" />
        &nbsp;&nbsp;{payoutStatus}
      </span>
    )
  } else if (payoutStatus === 'Failed') {
    payout_status = (
      <span>
        <BiErrorAlt size={20} color="#f44336" />
        &nbsp;&nbsp;{payoutStatus}
      </span>
    )
  } else if (sales > 0) {
    payout_status = (
      <span>
        <BiCheckCircle size={20} color="#4caf50" />
        &nbsp;&nbsp;{payoutStatus}
      </span>
    )
  } else {
    payout_status = (
      <span>
        <BiCircle size={20} color="#5A7D9A" />
        &nbsp;&nbsp;{payoutStatus}
      </span>
    )
  }

  const refreshView = () => (
    <label
      className="float-right refresh-btn"
      title="Update Summary"
      data-for="refreshSummary"
      data-tip="Update Summary"
      style={{ cursor: 'pointer', marginTop: '-10px' }}
      onClick={() => {
        setCount(count + 1)
        setRefreshing(true)
      }}
    >
      {refreshing ? 'Updating...' : ''} <BiRefresh size={30} />
      <ReactTooltip id="refreshSummary" place="top" type="dark" effect="solid" className="p-1" />
    </label>
  )

  /* ---------------- verification banner ---------------- */
  // Plain text only: React escapes it, and the backend sends a friendly sentence, never a code.
  const reasonLine = verReason ? (
    <>
      <br />
      {verReason}
    </>
  ) : null

  let verBanner = null
  if (verState === 'hold') {
    verBanner = (
      <div className="alert alert-warning">
        <strong>Advance on hold</strong>
        <br />
        Your advance payout is on hold for now. Your balance is safe and nothing is lost. Please
        contact support so we can help.
        {reasonLine}
      </div>
    )
  } else if (verState === 'window') {
    verBanner = (
      <div className="alert alert-info">
        <strong>Verification in progress</strong>
        <br />
        Your event has ended and is being verified.{' '}
        {verification?.opens_at_label
          ? `Payouts open from about ${verification.opens_at_label}.`
          : `Payouts open up to ${VERIFY_HOURS} hours after the event ends.`}{' '}
        No action is needed from you.
      </div>
    )
  } else if (verState === 'review') {
    verBanner = (
      <div className="alert alert-warning">
        <strong>Payout under review</strong>
        <br />
        We're verifying your event before releasing your payout. Your balance is safe and nothing is
        lost. We'll contact you if we need any additional information. Please contact support if you
        have questions.
        {reasonLine}
      </div>
    )
  } else if (partialVerified) {
    verBanner = (
      <div className="alert alert-info">
        <strong>Partially released</strong>
        <br />
        {verPct}% of your net sales is available to withdraw. The remaining {100 - verPct}% (
        {zar(held)}) will be released once verification is complete.
        {reasonLine}
      </div>
    )
  } else if (verState === 'verified') {
    verBanner = (
      <div className="alert alert-success">
        <strong>Event verified</strong>
        <br />
        Your event has been verified and your full balance is released.
      </div>
    )
  }

  /* ---------------- styles ---------------- */
  const card = {
    background: '#fff',
    border: '1px solid #e3e7ec',
    borderRadius: 10,
    padding: '14px 16px',
    height: '100%',
  }
  const cardLabel = {
    fontSize: 12,
    color: '#6c757d',
    textTransform: 'uppercase',
    letterSpacing: 0.4,
  }
  const cardValue = { fontSize: 24, fontWeight: 700, lineHeight: 1.2 }
  const sectionTitle = { fontSize: 15, fontWeight: 700, margin: '24px 0 8px' }

  const Line = ({ label, note, amount, sign, strong, muted, color }) => (
    <tr style={strong ? { background: '#f6f8fa' } : undefined}>
      <td className="td-bottom-line-map" style={{ width: '34%', fontWeight: strong ? 700 : 400 }}>
        {label}
      </td>
      <td
        className="td-bottom-line-map"
        style={{
          width: '18%',
          whiteSpace: 'nowrap',
          fontWeight: strong ? 700 : 400,
          color: color || (muted ? '#6c757d' : undefined),
        }}
      >
        {sign ? `${sign} ` : ''}
        {amount}
      </td>
      <td className="td-bottom-line-map text-12" style={{ color: '#6c757d' }}>
        {note}
      </td>
    </tr>
  )

  return (
    <>
      {/* Event name */}
      {(props.eventName || props.eventname || eventName) && (
        <div className="col-12 p-0">
          <div
            style={{
              margin: '8px 0 16px',
              padding: '12px 16px',
              background: '#f6f8fa',
              borderLeft: '4px solid #17a2b8',
              borderRadius: 6,
            }}
          >
            <div
              style={{
                fontSize: 12,
                color: '#6c757d',
                textTransform: 'uppercase',
                letterSpacing: 0.5,
              }}
            >
              Event
            </div>
            <div style={{ fontSize: 22, fontWeight: 700, lineHeight: 1.3 }}>
              {toTitleCase(props.eventName || props.eventname || eventName)}
            </div>
          </div>
        </div>
      )}

      {/* Info banner */}
      <div className="col-12 mt-2 p-0">
        <div className={sales > 0 ? 'alert alert-success' : 'alert alert-info'}>
          <strong>Note:</strong> This payout summary updates in real-time as new sales are
          confirmed. <br />
          {sales > 0 ? (
            <label className="text-success mb-0">
              {eventOver ? 'Sales are complete.' : 'Sales are in progress.'}
            </label>
          ) : (
            <label className="text-danger mb-0">
              {message ? message : 'No sales have been made yet.'}
            </label>
          )}
          {refreshView()}
        </div>
      </div>

      {/* Verification banner */}
      {verBanner && <div className="col-12 p-0">{verBanner}</div>}

      {/* Header + action */}
      <div className="col-12 p-0">
        <div className="d-flex flex-wrap align-items-center justify-content-between mb-3">
          <h5 className="mb-2 mb-lg-0">
            Payout Summary
            <VerificationBadge state={verState} />
            <button
              type="button"
              onClick={() => setInfoOpen(true)}
              title="How payouts work"
              aria-label="How payouts work"
              style={{
                background: 'none',
                border: 0,
                padding: 0,
                marginLeft: 10,
                lineHeight: 1,
                color: '#17a2b8',
                cursor: 'pointer',
                verticalAlign: 'middle',
              }}
            >
              <BiInfoCircle size={22} />
            </button>
          </h5>
          <Dropdown>
            <Dropdown.Toggle
              variant={canRequest ? 'info' : 'secondary'}
              className="text-bold"
              disabled={!canRequest}
            >
              Request payout
            </Dropdown.Toggle>
            <Dropdown.Menu>
              <Dropdown.Header style={{ whiteSpace: 'normal', maxWidth: 300 }}>
                {releaseLine}
              </Dropdown.Header>
              <Dropdown.Item
                onClick={() => requestPayout('partial')}
                className="text-decoration-none"
              >
                <strong>Partial payout</strong>
                <div className="text-12 text-muted">
                  Choose any amount up to {zar(netPayout)}. Fee {zar(FEE_LOW)}–{zar(FEE_HIGH)} per
                  request.
                </div>
              </Dropdown.Item>
              <Dropdown.Item onClick={() => requestPayout('full')} className="text-decoration-none">
                <strong>Full payout: {zar(netPayout)}</strong>
                <div className="text-12 text-muted">
                  Fee {zar(fullFee)} → you receive {zar(fullReceive)}
                </div>
              </Dropdown.Item>
            </Dropdown.Menu>
          </Dropdown>
        </div>

        {/* Stat cards */}
        <div className="row" style={{ marginLeft: -8, marginRight: -8 }}>
          <div className="col-lg-3 col-6 mb-3" style={{ paddingLeft: 8, paddingRight: 8 }}>
            <div style={card}>
              <div style={cardLabel}>Gross ticket sales</div>
              <div style={cardValue}>{zar(gross)}</div>
              <div className="text-12 mt-1">{isComplete}</div>
            </div>
          </div>
          <div className="col-lg-3 col-6 mb-3" style={{ paddingLeft: 8, paddingRight: 8 }}>
            <div style={card}>
              <div style={cardLabel}>Available to withdraw</div>
              <div style={{ ...cardValue, color: canRequest ? '#1b8a5a' : undefined }}>
                {zar(netPayout)}
              </div>
              <div className="text-12 mt-1 text-muted">{releaseLine}</div>
            </div>
          </div>
          <div className="col-lg-3 col-6 mb-3" style={{ paddingLeft: 8, paddingRight: 8 }}>
            <div style={card}>
              <div style={cardLabel}>You'd receive (full payout)</div>
              <div style={cardValue}>{canRequest ? zar(fullReceive) : '—'}</div>
              <div className="text-12 mt-1 text-muted">
                {canRequest
                  ? `After payout fee of ${zar(fullFee)}`
                  : onHold
                    ? 'Available once the hold is lifted'
                    : verifying
                      ? 'Available once verification is complete'
                      : `Available balance must be at least ${zar(MIN_PAYOUT)}`}
              </div>
            </div>
          </div>
          <div className="col-lg-3 col-6 mb-3" style={{ paddingLeft: 8, paddingRight: 8 }}>
            <div style={card}>
              <div style={cardLabel}>Payout status</div>
              <div style={{ ...cardValue, fontSize: 18, paddingTop: 4 }}>{payout_status}</div>
              <div className="text-12 mt-1 text-muted">Already requested: {zar(withdrawn)}</div>
            </div>
          </div>
        </div>

        <PayoutInfoModal isOpen={infoOpen} onClose={() => setInfoOpen(false)} />

        <PayoutModal
          isOpen={modalOpen}
          onClose={() => setModalOpen(false)}
          onConfirm={handlePayoutRequest}
          available={netPayout}
          payOutType={payOutType}
          sponsored={sponsored}
          loading={clipLoading}
          releasePercent={releasePercent}
          held={held}
          released={released}
          heldLabel={heldLabel}
          releaseSummary={releaseSummary}
          bank={bank}
        />
      </div>

      {/* Failed state */}
      {status === 'failed' ? (
        <div className="col-12 p-0">
          <div className="alert alert-danger">{message}</div>
        </div>
      ) : (
        <div className="col-12 p-0">
          {/* Release schedule */}
          <div style={sectionTitle}>Release schedule</div>
          <div style={card}>
            <div className="d-flex justify-content-between flex-wrap" style={{ fontSize: 14 }}>
              <span>
                <strong>Net after refunds:</strong> {zar(netAfterRefunds)}
              </span>
              <span style={{ color: '#6c757d' }}>
                {eventEnded ? 'Event ended' : 'Event in progress'}
              </span>
            </div>
            <div
              style={{
                display: 'flex',
                height: 14,
                borderRadius: 7,
                overflow: 'hidden',
                background: '#cfd6dd',
                margin: '10px 0',
              }}
            >
              <div style={{ width: `${releasePercent}%`, background: '#1b8a5a' }} />
            </div>
            <div className="d-flex justify-content-between flex-wrap" style={{ fontSize: 14 }}>
              <span>
                <span style={{ color: '#1b8a5a' }}>●</span> Released now ({releasePercent}%):{' '}
                <strong>{zar(released)}</strong>
              </span>
              <span>
                <span style={{ color: '#cfd6dd' }}>●</span> {heldLabel} ({100 - releasePercent}%):{' '}
                <strong>{zar(held)}</strong>
              </span>
            </div>
            <div className="text-12 mt-2" style={{ color: '#6c757d' }}>
              {releaseSummary} Both amounts update as sales and refunds change.
            </div>
          </div>

          {/* Calculation ledger */}
          <div style={sectionTitle}>How your available balance is calculated</div>
          <div className="table-responsive">
            <table className="table table-bordered">
              <tbody>
                <tr>
                  <th className="td-bottom-line-map">Description</th>
                  <th className="td-bottom-line-map">Amount</th>
                  <th className="td-bottom-line-map">Notes</th>
                </tr>
                <Line
                  label="Ticket sales"
                  amount={zar(net)}
                  note="Ticket price × quantity sold, as set by you, before refunds, payouts and fees."
                />
                <Line
                  label="Approved refunds"
                  sign="−"
                  amount={zar(refunded)}
                  color={refunded > 0 ? '#c0392b' : undefined}
                  note="Refunds that met your rules or Karamale's default policy and were approved by you."
                />
                <Line label="Net after refunds" sign="=" amount={zar(netAfterRefunds)} strong />
                <Line
                  label={`${heldLabel} (${100 - releasePercent}%)`}
                  sign="−"
                  amount={zar(held)}
                  color={held > 0 ? '#c0392b' : undefined}
                  note={
                    onHold
                      ? 'Released once the hold on your advance is lifted.'
                      : eventEnded
                        ? held > 0
                          ? 'Released once verification of your event is complete.'
                          : 'Nothing is held.'
                        : `${100 - ADVANCE_PERCENT}% is held until the event ends and is verified, then becomes available to withdraw.`
                  }
                />
                <Line
                  label={`Released to you now (${releasePercent}%)`}
                  sign="="
                  strong
                  amount={zar(released)}
                  note={releaseSummary}
                />
                <Line
                  label="Payouts already requested"
                  sign="−"
                  amount={zar(withdrawn)}
                  color={withdrawn > 0 ? '#c0392b' : undefined}
                  note="Pending, processing or paid payout requests. Deducted until processed."
                />
                <Line
                  label="Platform fee"
                  sign="−"
                  amount={sponsored ? zar(0) : zar(platformFee)}
                  color={!sponsored && platformFee > 0 ? '#c0392b' : undefined}
                  note={
                    sponsored
                      ? 'Waived: this event is sponsored by Karamale.'
                      : '3.5% commission on online ticket sales, deducted before payout. Physical tickets are paid upfront and are not charged here.'
                  }
                />
                <Line
                  label="Available to withdraw"
                  sign="="
                  amount={zar(netPayout)}
                  strong
                  note={
                    <span>
                      The most you can request right now. Status: <span>{payout_status}</span>
                    </span>
                  }
                />
              </tbody>
            </table>
          </div>

          {/* Payout fee */}
          <div style={sectionTitle}>Payout fee (charged on every payout request)</div>
          <div className="table-responsive">
            <table className="table table-bordered">
              <tbody>
                <tr>
                  <th className="td-bottom-line-map">Amount you request</th>
                  <th className="td-bottom-line-map">Payout fee</th>
                  <th className="td-bottom-line-map">Example</th>
                </tr>
                <tr>
                  <td className="td-bottom-line-map">
                    {zar(MIN_PAYOUT)} to {zar(FEE_TIER_THRESHOLD - 0.01)}
                  </td>
                  <td className="td-bottom-line-map">{zar(FEE_LOW)}</td>
                  <td className="td-bottom-line-map text-12">
                    Request {zar(150)} → fee {zar(FEE_LOW)} → you receive {zar(150 - FEE_LOW)}
                  </td>
                </tr>
                <tr>
                  <td className="td-bottom-line-map">{zar(FEE_TIER_THRESHOLD)} and above</td>
                  <td className="td-bottom-line-map">{zar(FEE_HIGH)}</td>
                  <td className="td-bottom-line-map text-12">
                    Request {zar(500)} → fee {zar(FEE_HIGH)} → you receive {zar(500 - FEE_HIGH)}
                  </td>
                </tr>
                <tr>
                  <td
                    colSpan={3}
                    className="td-bottom-line-map text-12"
                    style={{ color: '#6c757d' }}
                  >
                    Covers bank transaction fees and administration. It is deducted from the amount
                    you request, not from your remaining balance.{' '}
                    {sponsored
                      ? 'This event is sponsored by Karamale: the platform fee is waived, but the payout fee still applies.'
                      : 'Events sponsored by Karamale also pay this fee.'}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="text-12 mb-3" style={{ color: '#6c757d' }}>
            <strong>Important:</strong> All figures update in real-time as new sales are confirmed.
          </div>
        </div>
      )}

      <div className={alertState + ' text-center ' + errorDisplay}>{resMessage}</div>
    </>
  )
}

export default Summary
